/**
 * Projects visible to the signed-in user, read straight from the Go backend
 * via `GET /projects`.
 *
 * Why not reuse `api/projects.ts`? That module talks to the Backstage proxy
 * (`/api/proxy/ses-projects`) with a Bearer token, while every requirement
 * endpoint talks to the Go backend directly with the `ses_at` session cookie.
 * The requirement screens need a project list on the *same* auth path as the
 * calls they are about to make — mixing the two would make a screen depend on
 * both the proxy's header allow-list and the cookie, and the proxy currently
 * does not forward `Cookie` at all.
 *
 * `ListProjects` is scoped server-side to the actor's organization and user, so
 * this list is also what bounds which project ids the UI may act on.
 */

import { apiCall, API_BASE_URL } from '../utils/apiClient';
import { ProjectRole } from '../roles';
import { mapUserRole } from './projects';

/** A project the current user may work in. */
export interface AccessibleProject {
  id: number;
  name: string;
  description: string;
  /**
   * The user's role on the project. A project with no role of the user's own
   * is listed because they can read it (an organization admin), so it is a
   * Viewer.
   */
  role: ProjectRole;
}

interface ListProjectsResponseDto {
  projects?: Array<{
    // int64 fields arrive as JSON strings (protojson), not numbers.
    id?: string | number;
    name?: string;
    description?: string;
    role?: string;
  }>;
  total?: string | number;
}

/**
 * Narrow the wire shape to a usable project, or null when the id cannot be
 * trusted. The requirement APIs validate `projectId > 0`, so anything that does
 * not survive this conversion must never reach them — a NaN would otherwise be
 * sent as the string "NaN" and come back as an opaque 400.
 */
function toAccessibleProject(
  dto: NonNullable<ListProjectsResponseDto['projects']>[number],
): AccessibleProject | null {
  const id = Number(dto.id);
  if (!Number.isSafeInteger(id) || id <= 0) {
    return null;
  }
  return {
    id,
    name: dto.name ?? '',
    description: dto.description ?? '',
    role: mapUserRole(dto.role) ?? 'Viewer',
  };
}

/** Matches `maxPageSize` in internal/projects/list_projects.go. */
const PAGE_SIZE = 200;
/** Stops a bad `total` from looping forever; 20 pages is 4000 projects. */
const MAX_PAGES = 20;

/**
 * Fetch every project the user can see, following pagination to the end.
 *
 * Paging is not optional here: the backend defaults `page_size` to 50, and this
 * list doubles as the authorization gate for the requirement screens — a user
 * with more projects than one page would be told their own projects are not
 * available to them.
 *
 * Throws `ApiError` on a non-2xx response; a 401 is handled by `apiCall`
 * itself (redirect to login) and never returns here.
 */
export async function listAccessibleProjects(): Promise<AccessibleProject[]> {
  const collected: AccessibleProject[] = [];
  let page = 1;
  let total = Infinity;
  let fetched = 0;

  while (fetched < total && page <= MAX_PAGES) {
    const response = await apiCall(
      `${API_BASE_URL}/projects?page=${page}&pageSize=${PAGE_SIZE}`,
      { method: 'GET' },
    );
    const data = (await response.json()) as ListProjectsResponseDto;
    const batch = data.projects ?? [];
    if (batch.length === 0) break;

    collected.push(
      ...batch
        .map(toAccessibleProject)
        .filter((p): p is AccessibleProject => p !== null),
    );

    fetched += batch.length;
    const reportedTotal = Number(data.total);
    total = Number.isFinite(reportedTotal) ? reportedTotal : fetched;
    page += 1;
  }

  return collected;
}
